import { computed } from '@vue/composition-api'
import { PLANE_COLORS } from '../config/presentation'

const ALARMED_STATUSES = ['ALARMED', 'BURGLARY', 'ALARM', 'FAULT']

/**
 * Returns the first configured presentation value.
 * Alarm rules may include empty icon or color strings, which must not prevent
 * the device tag from supplying the normal visual fallback.
 */
const isConfiguredValue = (value) => {
  if (value === null || value === undefined) {
    return false
  }

  return typeof value !== 'string' || value.trim() !== ''
}

const firstConfiguredValue = (...values) => values.find(isConfiguredValue) ?? null

const isDmpAlarmActive = (latestAlarm, showAlarm) => {
  const status = (latestAlarm?.status ?? '').toUpperCase()

  return showAlarm
    || ALARMED_STATUSES.includes(status)
    || (status === 'BYPASSED' && Boolean(latestAlarm?.rule))
}

const isActiveAlarm = (latestAlarm, showAlarm) => (
  showAlarm || ALARMED_STATUSES.includes((latestAlarm?.status ?? '').toUpperCase())
)

const buildPresentation = (icon, iconColor, options = {}) => ({
  icon,
  iconColor,
  pulseColor: PLANE_COLORS.alarm,
  shouldPulse: false,
  ...options,
})

/**
 * Resolves non-DMP devices while preserving their existing tag-first behavior.
 */
const resolveStandardPresentation = ({ deviceTag, latestAlarm, showAlarm }) => {
  if (deviceTag?.icon) {
    return buildPresentation(deviceTag.icon, deviceTag.icon_color || PLANE_COLORS.defaultIcon)
  }

  if (!latestAlarm) {
    return buildPresentation(null, PLANE_COLORS.defaultIcon)
  }

  const tag = latestAlarm.tag?.icon ? latestAlarm.tag : null
  const isAlarmed = isActiveAlarm(latestAlarm, showAlarm)

  if (!isAlarmed) {
    return buildPresentation(tag?.icon ?? null, tag?.icon_color ?? PLANE_COLORS.defaultIcon)
  }

  const icon = firstConfiguredValue(latestAlarm.rule?.icon, tag?.icon)
  const color = firstConfiguredValue(latestAlarm.rule?.color, PLANE_COLORS.alarm)

  return buildPresentation(icon, color)
}

/**
 * Resolves DMP alarms using the rule only when its visual fields are set.
 * A tag remains the fallback for partially configured rules.
 */
const resolveDmpPresentation = ({ deviceTag, latestAlarm, showAlarm }) => {
  const backendPresentation = latestAlarm?.presentation

  if (backendPresentation) {
    return buildPresentation(
      backendPresentation.icon ?? null,
      backendPresentation.icon_color ?? PLANE_COLORS.defaultIcon,
      {
        pulseColor: backendPresentation.pulse_color ?? PLANE_COLORS.alarm,
        shouldPulse: backendPresentation.should_pulse === true,
        usesDefaultIcon: backendPresentation.uses_default_icon === true,
        shouldTintDefaultIcon: backendPresentation.should_tint_default_icon === true,
      }
    )
  }

  // Compatibility for events produced before the backend presentation contract.
  const tag = latestAlarm?.tag?.icon ? latestAlarm.tag : deviceTag
  const isAlarmed = isDmpAlarmActive(latestAlarm, showAlarm)

  if (!isAlarmed) {
    return buildPresentation(tag?.icon ?? null, tag?.icon_color ?? PLANE_COLORS.defaultIcon, {
      usesDefaultIcon: !tag?.icon,
      shouldTintDefaultIcon: false,
      shouldPulse: false,
    })
  }

  const ruleIcon = firstConfiguredValue(latestAlarm?.rule?.icon, tag?.icon)
  const ruleColor = firstConfiguredValue(latestAlarm?.rule?.color)
  const hasTag = Boolean(tag?.icon)
  const iconColor = ruleColor || (hasTag ? PLANE_COLORS.alarm : PLANE_COLORS.defaultIcon)

  return buildPresentation(ruleIcon, iconColor, {
    // Without a tag, null keeps the device's configured default icon.
    usesDefaultIcon: !ruleIcon,
    shouldTintDefaultIcon: !ruleIcon && Boolean(ruleColor),
    shouldPulse: isAlarmed,
  })
}

/**
 * Uses the backend DMP presentation contract when available. The fallback only
 * supports payloads sent by servers that have not yet been deployed.
 */
export const shouldShowDmpAlarm = (latestAlarm) => {
  if (latestAlarm?.presentation) {
    return latestAlarm.presentation.should_pulse === true
  }

  return isDmpAlarmActive(latestAlarm, false)
}

/**
 * Resolves the visual state for a panel device.
 *
 * A tag defines the normal appearance. During an active alarm, a rule can
 * replace its icon and color; the red pulse always remains an alarm indicator.
 */
export const resolveDevicePresentation = ({ deviceTag, latestAlarm, showAlarm, vendor }) => {
  // The panel snapshot is the source of truth when a placed proxy is generic.
  // This happens with older floor-plan entries created before DMP proxy types.
  const isDmpDevice = vendor === 'dmp' || latestAlarm?.provider === 'dmp'

  if (isDmpDevice) {
    return resolveDmpPresentation({ deviceTag, latestAlarm, showAlarm })
  }

  return resolveStandardPresentation({ deviceTag, latestAlarm, showAlarm })
}

export const useDevicePresentation = ({ device, latestAlarm, showAlarm }) => computed(() => {
  const deviceObj = typeof device === 'function' ? device() : device
  return resolveDevicePresentation({
    deviceTag:   deviceObj.device_tag,
    latestAlarm: latestAlarm.value,
    showAlarm:   showAlarm.value,
    vendor:      deviceObj.vendor,
  })
})
